import React, { Fragment } from "react";
import { createShortLink } from "actions/shortLink";
import { fetchMerchantAccountTaxRates } from "actions/taxRate";
import { fetchMerchantAccountShippingRates } from "actions/shippingRate";
import {
  fetchMerchantAccountProductsPrices,
  fetchMerchantAccountCouponsPromotionCodes
} from "actions/merchantAccount";
import { TAX_ID_COLLECTION_TYPE_STANDARD_OPTION } from "utils/constants/taxRate";
import { fetchAppCheckout } from "actions/app";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { Formik, useFormikContext } from "formik";
import Padder from "components/Padder";
import CouponsFields from "components/ShortLinkForm/Fields/Coupons";
import TaxRatesFields from "components/ShortLinkForm/Fields/TaxRates";
import PaymentFields from "components/ShortLinkForm/Fields/Payment";
import ShippingFields from "components/ShortLinkForm/Fields/Shipping";
import RedirectsFields from "components/ShortLinkForm/Fields/Redirects";
import TrialFields from "components/ShortLinkForm/Fields/Trial";
import { getShortLinkPreviewTitle } from "utils/shortLink";
import {
  ensureAppCheckout,
  ensureProductsPricesCouponsPromotionCodes,
  ensureTaxRates,
  ensureAttachments,
  ensureShippingRates
} from "utils/hooks";
import { fetchAttachments } from "actions/attachment";
import MenuNavHeader from "components/MenuNavHeader";
import isEmpty from "lodash/isEmpty";
import get from "lodash/get";
import {
  prepareCommonCheckoutResultInitialValues,
  validateCommonCombinationCheckout
} from "utils/combination";
import { STATE_KEYS } from "utils/constants/state";
import { SHORT_LINK_SETTINGS } from "utils/constants/shortLink";
import Label from "components/Form/fields/Label";
import InfoNotice from "components/InfoNotice";
import Textarea from "components/Form/fields/Textarea";
import { useCtxReferences } from "utils/selectors";
import {
  useFormCombinations,
  useManifestFormFields
} from "utils/hooks/manifest";
import ValidationsBar from "components/ValidationsBar";
import { VALIDATION_CHECKOUT_KEY } from "constants/validation";

const CANCEL_URL_NOTICES = {
  LIVE: "The cancel url is locked to the same value as the live page location.",
  UNPUBLISHED:
    "When published, the cancel url will be set to be the same value as the live page location."
};

const CommonCheckoutBuilder = ({ name, actions, goBack, hideBuilder }) => {
  ensureAppCheckout({
    fetchAppCheckout: actions.fetchAppCheckout
  });

  const {
    values: parentValues,
    setValues: setParentValues
  } = useFormikContext();
  const entityUUID = name.split(".").pop();

  const admin = get(parentValues, STATE_KEYS.MANIFEST.ADMIN);
  const { merchantAccount } = useCtxReferences();

  const formFields = useManifestFormFields();
  const combinations = useFormCombinations();

  const {
    products,
    prices,
    coupons
  } = ensureProductsPricesCouponsPromotionCodes({
    fetchMerchantAccountProductsPrices:
      actions.fetchMerchantAccountProductsPrices,
    fetchMerchantAccountCouponsPromotionCodes:
      actions.fetchMerchantAccountCouponsPromotionCodes
  });
  const { taxRates } = ensureTaxRates({
    fetchMerchantAccountTaxRates: actions.fetchMerchantAccountTaxRates
  });
  const { shippingRates } = ensureShippingRates({
    fetchMerchantAccountShippingRates: actions.fetchMerchantAccountShippingRates
  });
  ensureAttachments({
    fetchAttachments: actions.fetchAttachments
  });

  const submitForm = (formValues, formikActions) => {
    goBack(formValues.data);
    formikActions.setSubmitting(false);
  };

  const checkoutCommonPath = `${name}.${STATE_KEYS.FORM.CONFIG_CHECKOUT_COMMON}`;
  const checkoutCommon = get(parentValues, checkoutCommonPath);

  const checkoutConstantPath = `${name}.${STATE_KEYS.FORM.CONFIG_LOGIC_CHECKOUTS_CONSTANT}`;
  const checkoutConstant = get(parentValues, checkoutConstantPath);

  /**
   * Note:
   * Cancel url is locked to the same value as the live url
   * Page has to be published to a live url for this to be set
   */
  const hasAdminUrl = admin && admin.liveUrl;
  const sanitizedCancelUrl = hasAdminUrl ? admin.liveUrl.split("?")[0] : "";
  const initialValues = prepareCommonCheckoutResultInitialValues(
    checkoutCommon,
    {
      cancel_url: sanitizedCancelUrl
    },
    merchantAccount
  );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={submitForm}
      validate={validateCommonCombinationCheckout({
        uuid: entityUUID,
        constant: checkoutConstant,
        combinations,
        formFields,
        prices,
        products,
        prices,
        coupons,
        taxRates,
        shippingRates
      })}
    >
      {({ values, submitForm, isSubmitting, errors }) => {
        const previewSubtotal = getShortLinkPreviewTitle({
          shortLink: values,
          products,
          prices,
          coupons,
          taxRates,
          shippingRates
        });

        const hasValidationMessages = errors && errors.messages;
        const validationNode = hasValidationMessages ? (
          <ValidationsBar
            validations={errors.messages}
            noun="checkout"
            context={VALIDATION_CHECKOUT_KEY.COMMON}
            onFocus={hideBuilder}
            values={parentValues}
            setValues={setParentValues}
          />
        ) : null;

        return (
          <div className="w-100">
            <MenuNavHeader
              label={previewSubtotal}
              customClasses={{
                container:
                  "flex flex-column justify-between items-end sticky-l bb hairline-1 bg-white bh--inset top-0 z-5",
                content: "pb3 ph3 pl1 pt3 w-100 flex flex-row",
                label: "fw6 f3 word-break"
              }}
              action={{
                onClick: submitForm,
                copy: `Save`,
                disabled: !isEmpty(errors),
                loading: isSubmitting
              }}
              footer={validationNode}
            />
            <div className="ph3">
              {[
                <PaymentFields
                  name={STATE_KEYS.SHORT_LINK.CHECKOUT}
                  fieldset={[
                    SHORT_LINK_SETTINGS.PAYMENT_MODE,
                    SHORT_LINK_SETTINGS.PAYMENT_METHODS,
                    SHORT_LINK_SETTINGS.SUBSCRIPTION_SCHEDULE,
                    SHORT_LINK_SETTINGS.CAPTURE_METHOD,
                    SHORT_LINK_SETTINGS.SETUP_FUTURE_USAGE
                  ]}
                />,
                <TrialFields
                  name={STATE_KEYS.SHORT_LINK.CHECKOUT}
                  // Skip the requirements for a recurring product to be in the cart in order for trial period days to be allowed
                  skipRequirements
                />,
                <TaxRatesFields
                  name={STATE_KEYS.SHORT_LINK.CHECKOUT}
                  collectionTypeOptions={[
                    TAX_ID_COLLECTION_TYPE_STANDARD_OPTION
                  ]}
                />,
                <CouponsFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />,
                <ShippingFields name={STATE_KEYS.SHORT_LINK.CHECKOUT} />,
                <RedirectsFields
                  fieldset={[SHORT_LINK_SETTINGS.CHECKOUT_SUCCESS]}
                >
                  <div className="pt3">
                    <Label label="Cancel URL" />
                    {hasAdminUrl ? (
                      <Fragment>
                        <Textarea
                          value={sanitizedCancelUrl}
                          disabled
                          minRows={1}
                        />
                        <div className="pt2">
                          <InfoNotice copy={CANCEL_URL_NOTICES.LIVE} />
                        </div>
                      </Fragment>
                    ) : (
                      <InfoNotice copy={CANCEL_URL_NOTICES.UNPUBLISHED} />
                    )}
                  </div>
                </RedirectsFields>
              ].map((node, nodeIx) => (
                <div className="pt3 pt4-ns" key={nodeIx}>
                  {node}
                </div>
              ))}
            </div>
            <Padder theme="visible" />
          </div>
        );
      }}
    </Formik>
  );
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      createShortLink,
      fetchAppCheckout,
      fetchMerchantAccountProductsPrices,
      fetchMerchantAccountCouponsPromotionCodes,
      fetchMerchantAccountTaxRates,
      fetchMerchantAccountShippingRates,
      fetchAttachments
    },
    dispatch
  )
});

export default connect(null, mapDispatchToProps)(CommonCheckoutBuilder);
